import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { CircularProgress } from "@material-ui/core";
import { PRODUCT_STATUS } from "utils/constants";
import { BOOST_DATA_QUERY } from "./query";
import BoostSubscriptionCancel from "./BoostSubscriptionCancel";
import BoostSubscriptionDowngrade from "./BoostSubscriptionDowngrade";
import BoostSubscriptionPause from "./BoostSubscriptionPause";
import BoostSubscriptionStatus from "./BoostSubscriptionStatus";

const isDev = process.env.REACT_APP_NODE_ENV === "development";
const stripeCustomerDashUrl = `https://dashboard.stripe.com/${
  isDev ? "test/" : ""
}customers`;
const stripeSubscriptionDashUrl = `https://dashboard.stripe.com/${
  isDev ? "test/" : ""
}subscriptions`;

const StripeExternalLink = ({ label, value, url }) => {
  return (
    <div className="flex">
      {label && `${label}:`}
      <span className="ml-2 hover:text-boost-primary">
        <a
          className="font-bold hover:font-bold hover:text-boost-primary"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
          <i className="ml-2 fa-solid fa-arrow-up-right-from-square" />
        </a>
      </span>
    </div>
  );
};

const StripeExternalLinks = ({ customerId, subscriptionId }) => {
  return (
    <div className="flex flex-col">
      <StripeExternalLink
        value={customerId}
        url={`${stripeCustomerDashUrl}/${customerId}`}
      />
      <StripeExternalLink
        value={subscriptionId}
        url={`${stripeSubscriptionDashUrl}/${subscriptionId}`}
      />
    </div>
  );
};

const ActionContainer = ({ children, title }) => {
  return (
    <div className="flex flex-col items-start mt-4 p-2 pt-4 border border-gray-500 rounded relative">
      {!!title && (
        <div className="absolute top-0 -mt-3 px-2 bg-neutral-50 font-semibold cursor-default">
          {title}
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * @param {Object} props - Component props.
 * @param {string} props.email - (Required) Email of the user to pause the subscription.
 * @returns {React.ReactNode}
 */
const BoostSubscriptionManager = ({ email }) => {
  const { data: boostSubscriptionData, loading: loadingBoostData } = useQuery(
    BOOST_DATA_QUERY,
    { variables: { email } },
  );

  if (loadingBoostData) {
    return <CircularProgress />;
  }

  if (
    !boostSubscriptionData ||
    (boostSubscriptionData?.info?.status === "eligible" && !boostSubscriptionData?.info?.subscription)
  ) {
    return null;
  }

  const {
    status: productStatus,
    productTier,
    productTerm,
    subscription = {},
  } = boostSubscriptionData.info;
  const {
    id: stripeSubscriptionId,
    customerId: stripeCustomerId,
    willResumeAt,
    willCancelAt,
    billingCycleEndsAt,
    status: subscriptionStatus,
    scheduledUpdate = {},
  } = subscription;
  const endOfBillingCycle = billingCycleEndsAt
    ? new Date(billingCycleEndsAt * 1000)
    : null;

  const scheduledCancellationDate = willCancelAt
    ? new Date(willCancelAt * 1000)
    : null;

  const scheduledResumeDate = willResumeAt
    ? new Date(willResumeAt * 1000)
    : null;

  return (
    <div className="flex flex-col shadow-md p-4 rounded">
      <div>
        <h1 className="inline-block">Boost Subscription</h1>
      </div>
      <div className="mt-2">
        <BoostSubscriptionStatus
          productStatus={productStatus}
          scheduledCancellationDate={scheduledCancellationDate}
          scheduledResumeDate={scheduledResumeDate}
          scheduledUpdate={scheduledUpdate}
          productTerm={productTerm}
          productTier={productTier}
          subscription={subscription}
        />
      </div>
      <div className="flex flex-col mt-2 max-w-md">
        <ActionContainer title="Stripe Dashboard">
          <StripeExternalLinks
            customerId={stripeCustomerId}
            subscriptionId={stripeSubscriptionId}
          />
        </ActionContainer>
        {productStatus === PRODUCT_STATUS.ENROLLED ? (
          <>
            <ActionContainer title="Pause Subscription">
              <BoostSubscriptionPause
                email={email}
                scheduledCancellationDate={scheduledCancellationDate}
                scheduledResumeDate={scheduledResumeDate}
              />
            </ActionContainer>
            <ActionContainer title="Cancel Subscription">
              <BoostSubscriptionCancel
                email={email}
                scheduledCancellationDate={scheduledCancellationDate}
                scheduledResumeDate={scheduledResumeDate}
                scheduledUpdate={scheduledUpdate}
                subscriptionStatus={subscriptionStatus}
              />
            </ActionContainer>
            <ActionContainer title="Downgrade Subscription">
              <BoostSubscriptionDowngrade
                email={email}
                endOfBillingCycle={endOfBillingCycle}
                productStatus={productStatus}
                productTier={productTier}
                productTerm={productTerm}
                scheduledCancellationDate={scheduledCancellationDate}
                scheduledUpdate={scheduledUpdate}
                subscriptionStatus={subscriptionStatus}
              />
            </ActionContainer>
          </>
        ) : (
          <div className="mt-2 text-sm italic text-gray-700">
            you cannot manage subscriptions for terminated memberships
          </div>
        )}
      </div>
    </div>
  );
};

export default BoostSubscriptionManager;
